<section class="settings-page">
  <header class="page-header">
    <div class="page-title-wrap">
      <span class="eyebrow">Configuration</span>
      <h1>Business Settings</h1>
      <p>Manage store behaviour, appearance, payments, sellers and integrations from one place.</p>
    </div>

    <div class="header-actions">
      <button
        type="button"
        class="btn btn-secondary"
        (click)="loadSettings()"
        [disabled]="isLoading || isSavingAll"
      >
        <span class="button-icon" [class.spinning]="isLoading">↻</span>
        Refresh
      </button>

      <button
        type="button"
        class="btn btn-primary"
        (click)="saveAll()"
        [disabled]="dirtyCount === 0 || isSavingAll || isLoading"
      >
        <span class="button-icon" [class.spinning]="isSavingAll">{{ isSavingAll ? '↻' : '✓' }}</span>
        Save all
        <span *ngIf="dirtyCount" class="button-count">{{ dirtyCount }}</span>
      </button>
    </div>
  </header>

  <!-- <div class="stats-grid" aria-label="Settings summary">
    <article class="stat-card">
      <span class="stat-label">Total settings</span>
      <strong>{{ totalCount }}</strong>
    </article>

    <article class="stat-card">
      <span class="stat-label">Configured</span>
      <strong>{{ populatedCount }}</strong>
    </article>

    <article class="stat-card">
      <span class="stat-label">Empty values</span>
      <strong>{{ emptyCount }}</strong>
    </article>

    <article class="stat-card" [class.stat-card-warning]="dirtyCount > 0">
      <span class="stat-label">Unsaved changes</span>
      <strong>{{ dirtyCount }}</strong>
    </article>
  </div> -->

  <div *ngIf="errorMessage" class="alert alert-error" role="alert">
    <span>!</span>
    <p>{{ errorMessage }}</p>
    <button type="button" aria-label="Close error" (click)="errorMessage = ''">×</button>
  </div>

  <div *ngIf="successMessage" class="alert alert-success" role="status">
    <span>✓</span>
    <p>{{ successMessage }}</p>
    <button type="button" aria-label="Close message" (click)="successMessage = ''">×</button>
  </div>

  <div class="settings-layout">
    <aside class="settings-sidebar">
      <div class="search-box">
        <span class="search-icon">⌕</span>
        <input
          type="search"
          [(ngModel)]="searchTerm"
          placeholder="Search settings..."
          aria-label="Search business settings"
        />
        <button
          *ngIf="searchTerm"
          type="button"
          class="clear-search"
          aria-label="Clear search"
          (click)="clearSearch()"
        >
          ×
        </button>
      </div>

      <nav class="category-nav" aria-label="Settings categories">
        <!-- <button
          type="button"
          class="category-button"
          [class.active]="selectedCategory === 'all'"
          (click)="selectCategory('all')"
        >
          <span class="category-icon">▦</span>
          <span class="category-name">All settings</span>
          <span class="category-count">{{ categoryCount('all') }}</span>
        </button> -->

        <button
          *ngFor="let category of categories; trackBy: trackCategory"
          type="button"
          class="category-button"
          [class.active]="selectedCategory === category.key"
          (click)="selectCategory(category.key)"
        >
          <span class="category-icon">{{ category.icon }}</span>
          <span class="category-name">{{ category.label }}</span>
          <span class="category-count">{{ category.settings.length }}</span>
        </button>
      </nav>

      <button
        *ngIf="dirtyCount > 0"
        type="button"
        class="reset-all-button"
        (click)="resetAllChanges()"
        [disabled]="isSavingAll"
      >
        Reset all changes
      </button>
    </aside>

    <main class="settings-content">
      <div *ngIf="isLoading" class="loading-state" aria-live="polite">
        <div class="spinner"></div>
        <h2>Loading settings</h2>
        <p>Reading configuration from the server...</p>
      </div>

      <ng-container *ngIf="!isLoading">
        <section
          *ngFor="let category of visibleCategories; trackBy: trackCategory"
          class="category-section"
        >
          <div class="section-heading">
            <div class="section-icon">{{ category.icon }}</div>
            <div>
              <h2>{{ category.label }}</h2>
              <p>{{ category.description }}</p>
            </div>
            <span class="section-count">{{ category.settings.length }}</span>
          </div>

          <div class="settings-grid">
            <article
              *ngFor="let setting of category.settings; trackBy: trackSetting"
              class="setting-card"
              [class.is-dirty]="setting.dirty"
              [class.is-saving]="setting.saving"
            >
              <div class="setting-heading">
                <div>
                  <label [attr.for]="setting.type">{{ setting.label }}</label>
                  <code>{{ setting.type }}</code>
                </div>

                <span *ngIf="setting.dirty" class="changed-badge">Changed</span>
              </div>

              <div class="setting-control">
                <label
                  *ngIf="setting.inputType === 'switch'"
                  class="switch-control"
                  [attr.for]="setting.type"
                >
                  <input
                    [id]="setting.type"
                    type="checkbox"
                    [ngModel]="isSwitchEnabled(setting)"
                    (ngModelChange)="onValueChanged(setting, $event)"
                    [disabled]="setting.saving"
                  />
                  <span class="switch-track"><span class="switch-thumb"></span></span>
                  <span class="switch-value">
                    {{ isSwitchEnabled(setting) ? 'Enabled' : 'Disabled' }}
                  </span>
                </label>

                <div *ngIf="setting.inputType === 'color'" class="color-control">
                  <input
                    type="color"
                    class="color-picker"
                    [value]="displayValue(setting) || '#000000'"
                    (input)="onTextInput(setting, $any($event.target).value)"
                    [disabled]="setting.saving"
                    [attr.aria-label]="setting.label + ' colour picker'"
                  />
                  <input
                    [id]="setting.type"
                    type="text"
                    class="form-control"
                    [ngModel]="displayValue(setting)"
                    (ngModelChange)="onTextInput(setting, $event)"
                    placeholder="#000000"
                    [disabled]="setting.saving"
                  />
                </div>

                <textarea
                  *ngIf="setting.inputType === 'textarea' || setting.inputType === 'json'"
                  [id]="setting.type"
                  class="form-control textarea-control"
                  [class.json-control]="setting.inputType === 'json'"
                  [ngModel]="displayValue(setting)"
                  (ngModelChange)="onTextInput(setting, $event)"
                  [placeholder]="setting.inputType === 'json' ? 'Enter JSON value' : 'Enter value'"
                  [rows]="setting.inputType === 'json' ? 6 : 4"
                  [disabled]="setting.saving"
                  spellcheck="false"
                ></textarea>

                <input
                  *ngIf="setting.inputType === 'text' || setting.inputType === 'number'"
                  [id]="setting.type"
                  [type]="setting.inputType === 'number' ? 'number' : 'text'"
                  class="form-control"
                  [ngModel]="displayValue(setting)"
                  (ngModelChange)="onTextInput(setting, $event)"
                  [placeholder]="setting.inputType === 'number' ? 'Enter number' : 'Enter value'"
                  [disabled]="setting.saving"
                />
              </div>

              <p *ngIf="setting.saveError" class="field-error">{{ setting.saveError }}</p>

              <footer class="setting-footer">
                <span class="value-status" [class.has-value]="displayValue(setting) !== '' || setting.inputType === 'switch'">
                  {{ displayValue(setting) !== '' || setting.inputType === 'switch' ? 'Value available' : 'No value returned' }}
                </span>

                <div class="setting-actions">
                  <button
                    type="button"
                    class="card-button card-button-secondary"
                    (click)="resetSetting(setting)"
                    [disabled]="!setting.dirty || setting.saving"
                  >
                    Reset
                  </button>

                  <button
                    type="button"
                    class="card-button card-button-primary"
                    (click)="saveSetting(setting)"
                    [disabled]="!setting.dirty || setting.saving"
                  >
                    <span *ngIf="setting.saving" class="mini-spinner"></span>
                    {{ setting.saving ? 'Saving' : 'Save' }}
                  </button>
                </div>
              </footer>
            </article>
          </div>
        </section>

        <div *ngIf="visibleCategories.length === 0" class="empty-state">
          <div class="empty-icon">⌕</div>
          <h2>No settings found</h2>
          <p>Try another search term or choose a different category.</p>
          <button type="button" class="btn btn-secondary" (click)="clearSearch(); selectCategory('all')">
            Clear filters
          </button>
        </div>
      </ng-container>
    </main>
  </div>
</section>
